import { useAppStore } from '../stores/useAppStore';

export type SyncErrorKind =
  | 'not_configured'
  | 'auth'
  | 'network'
  | 'server'
  | 'not_found'
  | 'too_large';

export class SyncError extends Error {
  constructor(public kind: SyncErrorKind, message: string) {
    super(message);
    this.name = 'SyncError';
  }
}

function defaultBaseUrl(): string {
  // Vite injects BASE_URL as the build-time `base` ('/bedtimer/' here).
  // Caddy reverse-proxies /bedtimer/api/* to the backend, so derive the
  // API root from the document's origin + base path.
  const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  return window.location.origin + base + '/api';
}

function getConfig(): { baseUrl: string; token: string } {
  const s = useAppStore.getState().settings;
  if (!s.apiToken) {
    throw new SyncError('not_configured', 'Set a sync token in Settings.');
  }
  const override = s.apiBaseUrl?.trim().replace(/\/+$/, '');
  const baseUrl = override || defaultBaseUrl();
  return { baseUrl, token: s.apiToken };
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const { baseUrl, token } = getConfig();
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  let resp: Response;
  try {
    resp = await fetch(baseUrl + path, { ...init, headers });
  } catch (e) {
    throw new SyncError('network', `Network error: ${(e as Error).message}`);
  }
  return resp;
}

async function expectOk(resp: Response): Promise<Response> {
  if (resp.ok) return resp;
  if (resp.status === 401) throw new SyncError('auth', 'Bad sync token.');
  if (resp.status === 404) throw new SyncError('not_found', 'Not found.');
  if (resp.status === 413) throw new SyncError('too_large', 'Payload too large.');
  const body = await resp.text().catch(() => '');
  throw new SyncError(
    'server',
    `HTTP ${resp.status}${body ? ': ' + body.slice(0, 200) : ''}`
  );
}

export async function syncHealth(): Promise<{ ok: boolean }> {
  const r = await authedFetch('/health');
  await expectOk(r);
  return r.json();
}

export async function syncMe(): Promise<{ user: string }> {
  const r = await authedFetch('/me');
  await expectOk(r);
  return r.json();
}

export type ObjectListEntry = { id: string; updatedAt: number };
export type BulkObjectEntry<T> = {
  id: string;
  updatedAt: number;
  json: T;
};

export async function listObjects(
  kind: string,
  since?: number
): Promise<ObjectListEntry[]> {
  const q = since != null ? `?since=${since}` : '';
  const r = await authedFetch(`/objects/${kind}${q}`);
  await expectOk(r);
  const data = (await r.json()) as { items: ObjectListEntry[] };
  return data.items;
}

export async function listObjectsBulk<T>(
  kind: string,
  since?: number
): Promise<BulkObjectEntry<T>[]> {
  const params = new URLSearchParams({ bulk: '1' });
  if (since != null) params.set('since', String(since));
  const r = await authedFetch(`/objects/${kind}?${params}`);
  await expectOk(r);
  const data = (await r.json()) as { items: BulkObjectEntry<T>[] };
  return data.items;
}

export async function getObject<T>(
  kind: string,
  id: string
): Promise<{ json: T; updatedAt: number } | null> {
  const r = await authedFetch(`/objects/${kind}/${id}`);
  if (r.status === 404) return null;
  await expectOk(r);
  const data = (await r.json()) as { json: T; updatedAt: number };
  return { json: data.json, updatedAt: data.updatedAt };
}

export async function putObject<T>(
  kind: string,
  id: string,
  json: T
): Promise<{ updatedAt: number }> {
  const r = await authedFetch(`/objects/${kind}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ json }),
  });
  await expectOk(r);
  return r.json();
}

export async function deleteObject(kind: string, id: string): Promise<void> {
  const r = await authedFetch(`/objects/${kind}/${id}`, { method: 'DELETE' });
  if (r.status !== 204) await expectOk(r);
}

export type BlobHead = { size: number; mime?: string };

export async function headBlob(key: string): Promise<BlobHead | null> {
  const r = await authedFetch(`/blobs/${key}`, { method: 'HEAD' });
  if (r.status === 404) return null;
  await expectOk(r);
  const size = Number(r.headers.get('content-length') ?? 0);
  const mime = r.headers.get('content-type') ?? undefined;
  return { size, mime };
}

export async function getBlob(key: string): Promise<Blob | null> {
  const r = await authedFetch(`/blobs/${key}`);
  if (r.status === 404) return null;
  await expectOk(r);
  return r.blob();
}

export async function getBlobWithProgress(
  key: string,
  opts?: {
    signal?: AbortSignal;
    onProgress?: (loaded: number, total?: number) => void;
  }
): Promise<Blob | null> {
  const { baseUrl, token } = getConfig();
  let resp: Response;
  try {
    resp = await fetch(baseUrl + `/blobs/${key}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: opts?.signal,
    });
  } catch (e) {
    if ((e as DOMException)?.name === 'AbortError') throw e;
    throw new SyncError('network', `Network error: ${(e as Error).message}`);
  }
  if (resp.status === 404) return null;
  await expectOk(resp);
  const totalHeader = resp.headers.get('content-length');
  const total = totalHeader ? Number(totalHeader) : undefined;
  const mime = resp.headers.get('content-type') ?? undefined;
  if (!resp.body || !opts?.onProgress) {
    return resp.blob();
  }
  const reader = resp.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  opts.onProgress(0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      opts.onProgress(loaded, total);
    }
  }
  return new Blob(chunks as BlobPart[], mime ? { type: mime } : {});
}

export async function putBlob(
  key: string,
  body: Blob | string,
  mime?: string
): Promise<{ size: number; mime?: string }> {
  const headers: Record<string, string> = {};
  let payload: BodyInit;
  if (typeof body === 'string') {
    headers['Content-Type'] = mime ?? 'text/plain; charset=utf-8';
    payload = body;
  } else {
    headers['Content-Type'] = mime ?? body.type ?? 'application/octet-stream';
    payload = body;
  }
  const r = await authedFetch(`/blobs/${key}`, {
    method: 'PUT',
    headers,
    body: payload,
  });
  await expectOk(r);
  return r.json();
}

export async function deleteBlob(key: string): Promise<void> {
  const r = await authedFetch(`/blobs/${key}`, { method: 'DELETE' });
  if (r.status !== 204) await expectOk(r);
}

export function isSyncConfigured(): boolean {
  const s = useAppStore.getState().settings;
  return !!s.apiToken;
}

export type LlmStreamEvent = Record<string, unknown> & { type: string };

export async function llmMessages<T = unknown>(
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  const r = await authedFetch('/llm/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  await expectOk(r);
  return r.json();
}

export async function* llmStream(
  body: Record<string, unknown>,
  signal?: AbortSignal
): AsyncGenerator<LlmStreamEvent> {
  const { baseUrl, token } = getConfig();
  let resp: Response;
  try {
    resp = await fetch(baseUrl + '/llm/stream', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    throw new SyncError('network', `Network error: ${(e as Error).message}`);
  }
  if (!resp.ok) {
    if (resp.status === 401) throw new SyncError('auth', 'Bad sync token.');
    const txt = await resp.text().catch(() => '');
    throw new SyncError(
      'server',
      `HTTP ${resp.status}${txt ? ': ' + txt.slice(0, 200) : ''}`
    );
  }
  const stream = resp.body;
  if (!stream) throw new SyncError('server', 'no stream body');
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl = buf.indexOf('\n');
    while (nl >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      nl = buf.indexOf('\n');
      if (!line) continue;
      try {
        yield JSON.parse(line) as LlmStreamEvent;
      } catch {
        continue;
      }
    }
  }
  const tail = buf.trim();
  if (tail) {
    try {
      yield JSON.parse(tail) as LlmStreamEvent;
    } catch {
      // ignore
    }
  }
}

export async function ttsSynthesize(body: {
  key: string;
  voice_id: string;
  model_id: string;
  text: string;
  voice_settings?: Record<string, unknown>;
}, signal?: AbortSignal): Promise<{ key: string; size: number; mime?: string }> {
  const r = await authedFetch('/tts/synthesize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  await expectOk(r);
  return r.json();
}

export async function ttsVoices(signal?: AbortSignal): Promise<{ voices?: unknown[] }> {
  const r = await authedFetch('/tts/voices', { signal });
  await expectOk(r);
  return r.json();
}
